import { ReportAnswer } from '@/core/models/report.model'
import { useGetReportsQuery, useGetTemplatesQuery } from '@/core/services/report'
import { Alert, Divider, TextField, Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'
import CreateReportModal from './CreateReportModal'
import { Consultation } from '@/core/models/consultation.model'
import UpdatePatientRecord from './UpdatePatientRecord'
import { UpdateReportWithTemplate } from './UpdateReportWithTemplate'
import { Trans } from '@lingui/macro'
import _ from 'lodash'
import { useReportLicenseEnabled } from '@/features/report'
import { useGetConsultationQuery } from '@/core/services/consultation'

export type ReportViewerProps = {
  consultationId: string
  userId: string
}

export function ReportViewer({ consultationId, userId }: ReportViewerProps) {
  const { data: consultation } = useGetConsultationQuery(consultationId, { refetchOnMountOrArgChange: true })
  const hasLicense = useReportLicenseEnabled()

  const { data: reports } = useGetReportsQuery(
    { transcriptionId: consultation?.transcription },
    {
      skip: !consultation?.transcription,
      refetchOnMountOrArgChange: true,
    }
  )

  const { data: defaultTemplates } = useGetTemplatesQuery({ userId, isDefault: true }, { refetchOnMountOrArgChange: true })

  const report = reports?.[0]

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        {!report && hasLicense && consultation?.transcription && <CreateReportModal userId={userId} consultation={consultation} />}
        {report && hasLicense && (
          <>
            <UpdatePatientRecord report={report} />
            <UpdateReportWithTemplate reportId={report._id} userId={userId} transcriptionId={consultation?.transcription!} />
          </>
        )}
      </Box>
      <Typography variant="h6" gutterBottom color="primary">
        <Trans>Medical report</Trans>
      </Typography>
      {hasLicense && !defaultTemplates?.length && (
        <Alert variant="outlined" severity="info">
          <Trans>Create a default report template so that the report is automatically generated</Trans>
        </Alert>
      )}

      {!hasLicense && (
        <Alert variant="outlined" severity="warning">
          <Trans>Request the license from your Admin to have the right to generate the report</Trans>
        </Alert>
      )}
      <Divider sx={{ mb: 1 }} />
      {report && report?.responses?.length > 0 && (
        <Stack spacing={2}>
          {report.responses.map((section: ReportAnswer, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="body1" color="primary" gutterBottom fontWeight="bold">
                {section.sectionName}
              </Typography>
              <TextField
                multiline
                fullWidth
                value={section.response}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
